import { AfterViewInit, Directive, ElementRef, Input, Renderer2 } from '@angular/core';
import each from 'lodash-es/each';
import { IZippyChangeEvent, IZippyEvent, ZippyAbstract, ZippyEventType } from '@df/legacy/ui/zippy/zippy.abstract';

/**
 * Creates directive that applies classes to element depending on zippy state
 *
 * ```html
 * <div zippyClass="active-class second-active-class !inative-class" zippyName="someGroup.somName"></div>
 * ```
 *
 * Note that if this is put on zippyOpen and dont need to provide name.
 *
 */
@Directive({
    selector: '[zippyClass]',
    standalone: false
})
export class ZippyClassDirective extends ZippyAbstract implements AfterViewInit {
    /**
     * Buffer not to trigger changes when not needed
     */
    protected visible?: boolean;

    /**
     * Class map: class name => show when  on (true) or when off (false)
     */
    protected classMap?: {
        [className: string]: boolean;
    };

    /**
     * Respect zippyOpen attribute for name parsing
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input() set zippyOpen(name: string | (string | number)[]) {
        this.parseName(name);
    }

    /**
     * Respect zippy name attribute for name parsing
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input() set zippyName(name: string | (string | number)[]) {
        this.parseName(name);
    }

    /**
     * Respect zippy tioggle attribute for name parsing
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input() set zippyToggle(name: string | (string | number)[]) {
        this.parseName(name);
    }

    /**
     * Respect zippy tioggle attribute for name parsing
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input() set zippyClose(name: string | (string | number)[]) {
        this.parseName(name);
    }

    /**
     * Sets class map on attr changes
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    @Input() set zippyClass(classString: string) {
        this.setZippyClass(classString);
    }

    constructor(
        protected ngEl: ElementRef,
        protected renderer: Renderer2
    ) {
        super();
    }

    protected setZippyClass(classString: string) {
        this.parse(classString);
    }

    ngAfterViewInit(): void {
        this.initClasses();
    }

    protected initClasses() {
        this.applyClasses(this.isOpen());
    }

    /**
     * Parses class string into map
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    protected parse(classString: string) {
        const classMap = {};
        if (classString) {
            classString.split(' ').forEach(className => {
                if (className) {
                    if (className[0] === '!') {
                        classMap[className.substr(1)] = false;
                    } else {
                        classMap[className] = true;
                    }
                }
            });
        }
        this.classMap = classMap;
    }

    /**
     * Event listener calling class to be applied when needed
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {IZippyEvent | IZippyChangeEvent} event
     */
    protected onEvent(event: IZippyEvent | IZippyChangeEvent) {
        if (!event.stopped) {
            if (event.type === ZippyEventType.change) {
                if (this.checkEventMatchExact(event)) {
                    this.applyClasses((<IZippyChangeEvent>event).visible);
                }
            }
        }
    }

    /**
     * Applies classes from classMap depengin on request state
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {boolean} visible [description]
     */
    protected applyClasses(visible: boolean) {
        if (this.visible !== visible) {
            this.visible = visible;
            each(this.classMap, (state: boolean, className: string) => {
                visible === state
                    ? this.renderer.addClass(this.ngEl.nativeElement, className)
                    : this.renderer.removeClass(this.ngEl.nativeElement, className);
            });
        }
    }
}
